import { AuditLogChange } from '../models/auditLog';

/** One field's before/after values, formatted for display. */
export interface ChangeRow {
  field: string;
  old: string;
  new: string;
}

/** Result of parsing an AuditLog.changes JSON string. */
export interface ParsedChanges {
  rows: ChangeRow[];
  /** True when the JSON could not be parsed; callers should fall back to showing the raw text. */
  parseError: boolean;
}

/** Render a JSON value for display: null/undefined as a dash, strings verbatim, else stringified. */
export function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
}

/**
 * Parses the raw per-field diff JSON ({ "Field": { "old": x, "new": y } }) into display rows,
 * sorted by field name. Shared by the single-entry and change-set audit dialogs.
 */
export function parseAuditChanges(changes: string | null | undefined): ParsedChanges {
  try {
    const parsed = JSON.parse(changes ?? '') as Record<string, AuditLogChange>;
    const rows = Object.entries(parsed ?? {})
      .map(([field, change]) => ({
        field,
        old: formatChangeValue(change?.old),
        new: formatChangeValue(change?.new)
      }))
      .sort((a, b) => a.field.localeCompare(b.field));
    return { rows, parseError: false };
  } catch {
    return { rows: [], parseError: true };
  }
}
